import { type WheelEvent } from 'react';

import { reset } from './reset.js';
import type {
  DragEndProps,
  DragMoveProps,
  DragStartProps,
  PointerEventModifiers,
  PointerEventPosition,
} from '../../../types/pointer-events.js';
import {
  panDragEndWithMiddleButton,
  panDragMoveWithMiddleButton,
  panDragStartWithMiddleButton,
} from '../interactions/pan/dragInteractions.js';
import {
  zoomDragEnd,
  zoomDragMove,
  zoomDragStart,
} from '../interactions/zoom/dragInteractions.js';
import {
  zoomKeyboardDown,
  zoomKeyboardEnter,
  zoomKeyboardEscape,
  zoomKeyboardUp,
} from '../interactions/zoom/keyboardInteractions.js';
import {
  zoomMouseClickOrTap,
  zoomMouseHover,
  zoomMouseWheel,
} from '../interactions/zoom/mouseInteractions.js';
import type {
  InteractionsHandlers,
  InteractionTriggers,
  zoomInteractions,
} from '../types.js';

export const getZoomInteractions = ({
  cursorMode,
  selectionArea,
  eventCancelled,
  onSelectEnd,
  lastMousePositionRef,
  pinned,
  onHover,
  onTogglePin,
  setCursorMode,
  height,
  onSelectChange,
  onSelectStart,
  onReset,
  width,
  onZoomChange,
  onPanChange,
  panDeltaRef,
  onPanStart,
  onPanEnd,
}: zoomInteractions): InteractionsHandlers => {
  return {
    onMouseHover: (event: PointerEventPosition & PointerEventModifiers) => {
      if (selectionArea) {
        return;
      }
      zoomMouseHover({
        event,
        pinned,
        lastMousePositionRef,
        cursorMode,
        setCursorMode,
        onHover,
      });
    },
    onMouseClickOrTap: (event: PointerEventPosition & PointerEventModifiers) =>
      zoomMouseClickOrTap({
        event,
        width,
        lastMousePositionRef,
        onZoomChange,
      }),
    onDragStart: (event: DragStartProps & PointerEventModifiers) =>
      zoomDragStart({
        event,
        onSelectStart,
      }),
    onDragMove: (event: DragMoveProps & PointerEventModifiers) =>
      zoomDragMove({ event, height, width, onSelectChange }),
    onDragEnd: (event: DragEndProps & PointerEventModifiers) =>
      zoomDragEnd({
        event,
        height,
        width,
        lastMousePositionRef,
        onSelectEnd,
        onZoomChange,
      }),
    onDoubleClickOrTap: (triggeredBy: InteractionTriggers) => {
      onReset?.({
        triggeredBy,
      });

      reset(lastMousePositionRef);
    },
    onWheel: (event: WheelEvent) =>
      zoomMouseWheel({
        event,
        lastMousePositionRef,
        width,
        onZoomChange,
        onPanChange,
      }),
    onKeyEscape: () =>
      zoomKeyboardEscape({
        selectionArea,
        eventCancelled,
        onSelectEnd,
        lastMousePositionRef,
        pinned,
        onHover,
        onTogglePin,
      }),
    onKeyEnter: () =>
      zoomKeyboardEnter({
        selectionArea,
        lastMousePositionRef,
        width,
        onSelectEnd,
        onZoomChange,
        onHover,
      }),
    onKeyRight: () => {
      return;
    },
    onKeyLeft: () => {
      return;
    },
    onKeyUp: (isTemporaryZoomMode: boolean) => {
      if (isTemporaryZoomMode) {
        zoomKeyboardUp({ width, onZoomChange });
      }
    },
    onKeyDown: (isTemporaryZoomMode: boolean) => {
      if (isTemporaryZoomMode) {
        zoomKeyboardDown({ width, onZoomChange });
      }
    },
    onDragStartWithMiddleButton: () =>
      panDragStartWithMiddleButton({ panDeltaRef, onPanStart }),
    onDragMoveWithMiddleButton: (
      event: DragMoveProps & PointerEventModifiers,
    ) => panDragMoveWithMiddleButton({ event, panDeltaRef, onPanChange }),
    onDragEndWithMiddleButton: (event: DragEndProps & PointerEventModifiers) =>
      panDragEndWithMiddleButton({
        event,
        panDeltaRef,
        lastMousePositionRef,
        onPanEnd,
      }),
  };
};
